// ** React Imports
import { useEffect, useState } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux'

// ** Hooks
import { useSettings } from 'src/@core/hooks/useSettings'

// ** Types
import { RootState, AppDispatch } from 'src/store'
import { CalendarColors, CalendarFiltersType } from 'src/types/apps/calendarTypes'

// ** FullCalendar & App Components Imports
import Calendar from 'src/views/apps/schedule/calendar/Calendar'
import SidebarLeft from 'src/views/apps/schedule/calendar/SidebarLeft'
import CalendarWrapper from 'src/@core/styles/libs/fullcalendar'
import AddEventSidebar from 'src/views/apps/schedule/calendar/AddEventSidebar'

// ** Actions
import {
    addEvent,
    fetchEvents,
    deleteEvent,
    updateEvent,
    handleSelectEvent,
    handleAllCalendars,
    handleCalendarsUpdate,
    handleChannelUpdate
} from 'src/store/apps/calendar'

// ** Third Party Styles Imports
import 'react-datepicker/dist/react-datepicker.css'

// ** CalendarColors
const calendarsColor: CalendarColors = {
    Scheduled: 'primary',
    Delivered: 'secondary',
    Published : 'success',
    Error: 'error',
    Pending : 'warning'
}

const AppCalendar = () => {
    // ** States
    const [showPostContentDialog, setShowPostContentDialog] = useState<boolean>(false)
    const [calendarApi, setCalendarApi] = useState<null | any>(null)
    const [leftSidebarOpen, setLeftSidebarOpen] = useState<boolean>(false)
    const [addEventSidebarOpen, setAddEventSidebarOpen] = useState<boolean>(false)

    // ** Hooks
    const { settings } = useSettings()
    const dispatch = useDispatch<AppDispatch>()
    const store = useSelector((state: RootState) => state.calendar)
    const [randomState,setRandomState] = useState<number>(0);//Just for triggering interval with correct store data
    const rootStore = useSelector((state: RootState) => state)
    // ** Vars
    const leftSidebarWidth = 260
    const addEventSidebarWidth = 400
    const { skin, direction } = settings
    const mdAbove = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))

    const filterData = () => {
        const selectedChannelIds = store.selectedChannels.filter((ch: any) => ch.value != null).map((ch: any) => ch.value);
        dispatch(fetchEvents({ calendars: store.selectedCalendars as CalendarFiltersType[], channels: selectedChannelIds }))
    }

    useEffect(() => {
        filterData();
    }, [dispatch, store.selectedCalendars, store.selectedChannels, randomState])

    useEffect(() => {
        const interval = setInterval(() => {
            setRandomState((ps) => ps + 1);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const handleLeftSidebarToggle = () => setLeftSidebarOpen(!leftSidebarOpen)

    const handleAddEventSidebarToggle = () => setAddEventSidebarOpen(!addEventSidebarOpen)

    return (
        <CalendarWrapper
            className='app-calendar'
            sx={{
                boxShadow: skin === 'bordered' ? 0 : 6,
                ...(skin === 'bordered' && { border: theme => `1px solid ${theme.palette.divider}` })
            }}
        >
            <SidebarLeft
                store={store}
                mdAbove={mdAbove}
                dispatch={dispatch}
                calendarsColor={calendarsColor}
                leftSidebarOpen={leftSidebarOpen}
                leftSidebarWidth={leftSidebarWidth}
                handleSelectEvent={handleSelectEvent}
                handleAllCalendars={handleAllCalendars}
                handleCalendarsUpdate={handleCalendarsUpdate}
                handleChannelUpdate={handleChannelUpdate}
                handleLeftSidebarToggle={handleLeftSidebarToggle}
                handleAddEventSidebarToggle={handleAddEventSidebarToggle}
                setShowPostContentDialog={setShowPostContentDialog}
                rootStore={rootStore}
            />
            <Box
                sx={{
                    pb: 5,
                    px: 5,
                    pt: 2.25,
                    flexGrow: 1,
                    borderRadius: 1,
                    boxShadow: 'none',
                    backgroundColor: 'background.paper',
                    ...(mdAbove ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : {})
                }}
            >
                <Calendar
                    store={store}
                    dispatch={dispatch}
                    direction={direction}
                    updateEvent={updateEvent}
                    calendarApi={calendarApi}
                    calendarsColor={calendarsColor}
                    setCalendarApi={setCalendarApi}
                    handleSelectEvent={handleSelectEvent}
                    handleLeftSidebarToggle={handleLeftSidebarToggle}
                    handleAddEventSidebarToggle={handleAddEventSidebarToggle}
                    showPostContentDialog={showPostContentDialog}
                    setShowPostContentDialog={setShowPostContentDialog}
                />
            </Box>
            <AddEventSidebar
                store={store}
                dispatch={dispatch}
                addEvent={addEvent}
                updateEvent={updateEvent}
                deleteEvent={deleteEvent}
                calendarApi={calendarApi}
                drawerWidth={addEventSidebarWidth}
                handleSelectEvent={handleSelectEvent}
                addEventSidebarOpen={addEventSidebarOpen}
                handleAddEventSidebarToggle={handleAddEventSidebarToggle}
            />
        </CalendarWrapper>
    )
}

export default AppCalendar
